/* Marketing Momentum: native Shopify cart submission; no external dependencies. */
(() => {
  if (customElements.get('sky-marketing-momentum')) return;

  class MarketingMomentum extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const data = this.querySelector('[data-smm-variants]');
      if (!data) {
        this.startFrame = requestAnimationFrame(() => this.connectedCallback());
        return;
      }
      try {
        this.variants = JSON.parse(data.textContent);
        this.manualNote = JSON.parse(this.querySelector('[data-smm-manual-note]').textContent);
      } catch (error) {
        // Keep the server-rendered native product form usable if data is malformed.
        return;
      }
      this.ready = true;
      this.controller = new AbortController();
      this.variantInput = this.querySelector('[data-smm-variant]');
      this.planInput = this.querySelector('[data-smm-plan]');
      this.planBox = this.querySelector('[data-smm-plans]');
      this.billing = this.querySelector('[data-smm-billing]');
      this.addButton = this.querySelector('[data-smm-add]');
      this.addLabel = this.querySelector('[data-smm-add-label]');
      const options = { signal: this.controller.signal };
      this.variantInput.addEventListener('change', () => {
        this.selectVariant(this.planInput.value);
        this.updateURL();
      }, options);
      this.planInput.addEventListener('change', () => {
        this.updatePurchase();
        this.updateURL();
      }, options);
      window.addEventListener('pageshow', () => this.selectVariant(this.planInput.value), options);
      this.selectVariant(this.dataset.initialPlan || '');
      this.initSticky();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.startFrame);
      this.controller?.abort();
      this.heroObserver?.disconnect();
      this.bookObserver?.disconnect();
      this.ready = false;
    }

    selectVariant(preferredPlan) {
      this.variant = this.variants.find(item => item.id === this.variantInput.value);
      if (!this.variant) {
        this.addButton.disabled = true;
        this.addLabel.textContent = 'Currently unavailable';
        return;
      }
      const plans = this.variant.plans;
      this.planInput.replaceChildren();
      if (!this.variant.requiresPlan) {
        this.planInput.add(new Option('Pay for the first month', ''));
      }
      plans.forEach(plan => this.planInput.add(new Option(`${plan.name} — ${plan.price}`, plan.id)));
      this.planInput.value = plans.some(plan => plan.id === preferredPlan)
        ? preferredPlan
        : (this.variant.requiresPlan ? (plans[0]?.id || '') : '');
      this.planInput.required = this.variant.requiresPlan;
      this.planInput.disabled = plans.length === 0;
      this.planBox.hidden = plans.length === 0;
      this.updatePurchase();
    }

    addBillingLine(text, emphasis = false) {
      const p = document.createElement('p');
      const target = emphasis ? document.createElement('strong') : p;
      target.textContent = text;
      if (emphasis) p.append(target);
      this.billing.append(p);
    }

    updatePurchase() {
      if (!this.variant) return;
      const plan = this.variant.plans.find(item => item.id === this.planInput.value);
      const available = this.variant.available && (!this.variant.requiresPlan || Boolean(plan));
      this.querySelectorAll('[data-smm-price]').forEach(el => {
        el.textContent = plan ? plan.price : this.variant.price;
      });
      this.querySelectorAll('[data-smm-price-period]').forEach(el => {
        el.textContent = plan ? 'Initial plan price' : 'per month';
      });
      this.addButton.disabled = !available;
      this.addLabel.textContent = available ? this.dataset.addLabel : 'Currently unavailable';
      this.billing.replaceChildren();
      if (plan) {
        this.addBillingLine(plan.name, true);
        if (plan.description) this.addBillingLine(plan.description);
        this.addBillingLine(`Due at checkout: ${plan.due}.${plan.remaining ? ` Remaining balance: ${plan.remaining}.` : ''}`);
        if (plan.adjustments.length > 1) {
          plan.adjustments.forEach((adjustment, index) => {
            const duration = adjustment.orders
              ? ` for ${index === 0 ? 'the first ' : 'the next '}${adjustment.orders} billing cycle${adjustment.orders === 1 ? '' : 's'}`
              : ' per billing cycle';
            this.addBillingLine(`${index === 0 ? '' : 'Then '}${adjustment.price}${duration}.`);
          });
        }
        this.addBillingLine('Future charges follow the selected plan shown at checkout. Three-month minimum commitment applies.');
      } else if (this.variant.requiresPlan) {
        this.addBillingLine('This option is currently unavailable. Please contact us to arrange your package.');
      } else {
        this.addBillingLine(this.manualNote);
      }
    }

    updateURL() {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.variantInput.value);
      if (this.planInput.value && !this.planInput.disabled) {
        url.searchParams.set('selling_plan', this.planInput.value);
      } else {
        url.searchParams.delete('selling_plan');
      }
      // Preserve Shopify's theme-preview and market parameters.
      history.replaceState(history.state, '', url);
    }

    initSticky() {
      const sticky = this.querySelector('[data-smm-sticky]');
      if (!sticky || !('IntersectionObserver' in window)) return;
      let pastHero = false;
      let bookingVisible = false;
      const sync = () => {
        const show = pastHero && !bookingVisible;
        sticky.classList.toggle('smm-show', show);
        sticky.inert = !show;
        sticky.setAttribute('aria-hidden', String(!show));
      };
      this.heroObserver = new IntersectionObserver(([entry]) => {
        pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        sync();
      });
      this.bookObserver = new IntersectionObserver(([entry]) => {
        bookingVisible = entry.isIntersecting;
        sync();
      });
      this.heroObserver.observe(this.querySelector('.smm-hero'));
      this.bookObserver.observe(this.querySelector('.smm-booking'));
    }
  }

  customElements.define('sky-marketing-momentum', MarketingMomentum);
})();
